import React from "react";

export const PageHeader = ({ title, subtitle, action, eyebrow }) => (
  <div className="flex items-start justify-between gap-4 mb-6">
    <div>
      {eyebrow && (
        <div className="text-xs uppercase tracking-[0.25em] text-cyan-300 font-medium mb-2" data-testid="page-eyebrow">
          {eyebrow}
        </div>
      )}
      <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight" data-testid="page-title">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-slate-400 max-w-2xl" data-testid="page-subtitle">{subtitle}</p>}
    </div>
    {action && <div className="shrink-0">{action}</div>}
  </div>
);

export const Card = ({ children, className = "", padded = true, ...rest }) => (
  <div
    className={`glass-card rounded-lg ${padded ? "p-5 md:p-6" : ""} ${className}`}
    {...rest}
  >
    {children}
  </div>
);

export const Pill = ({ status = "draft", children }) => {
  const cls = {
    Active: "pill-active",
    Paused: "pill-paused",
    Draft: "pill-draft",
    Completed: "pill-completed",
    "Pending approval": "pill-pending",
    Pending: "pill-pending",
    Rejected: "pill-rejected",
    Approved: "pill-active",
    Lead: "pill-pending",
    Churned: "pill-rejected",
  }[status] || "pill-draft";
  return <span className={`pill ${cls}`}>{children || status}</span>;
};

export const Btn = ({ children, variant = "primary", className = "", ...rest }) => {
  const variants = {
    primary: "bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold hover:opacity-90",
    secondary: "bg-white/5 border border-white/10 text-white hover:bg-white/10",
    ghost: "text-slate-300 hover:bg-white/5 hover:text-white",
    danger: "bg-rose-500/10 border border-rose-500/30 text-rose-300 hover:bg-rose-500/20",
  };
  return (
    <button
      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-all ${variants[variant]} ${className}`}
      {...rest}
    >{children}</button>
  );
};

export const Input = ({ label, hint, error, className = "", ...rest }) => (
  <div className={className}>
    {label && <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">{label}</label>}
    <input
      className="w-full bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white focus:border-cyan-400/60 focus:outline-none placeholder-slate-500 hover:border-white/25 transition-colors"
      {...rest}
    />
    {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
    {error && <div className="mt-1 text-xs text-rose-300">{error}</div>}
  </div>
);

export const Select = ({ label, children, className = "", ...rest }) => (
  <div className={className}>
    {label && <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">{label}</label>}
    <select
      className="w-full bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white focus:border-cyan-400/60 focus:outline-none hover:border-white/25 transition-colors"
      {...rest}
    >{children}</select>
  </div>
);

export const Textarea = ({ label, ...rest }) => (
  <div>
    {label && <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">{label}</label>}
    <textarea
      className="w-full bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white focus:border-cyan-400/60 focus:outline-none placeholder-slate-500 hover:border-white/25 transition-colors min-h-[80px]"
      {...rest}
    />
  </div>
);

export const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="text-center py-16 px-4">
    {Icon && <Icon className="w-10 h-10 mx-auto text-slate-600 mb-3" />}
    <div className="font-display text-lg font-semibold text-slate-200">{title}</div>
    {description && <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">{description}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const Stat = ({ label, value, change, icon: Icon, testid }) => (
  <Card data-testid={testid} className="stat-card">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">{label}</div>
        <div className="stat-value mt-2 font-display text-3xl font-bold tracking-tight">{value}</div>
        {change != null && (
          <div className={`mt-1 text-xs font-medium ${change >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
            {change >= 0 ? "▲" : "▼"} {Math.abs(change)}% vs last period
          </div>
        )}
      </div>
      {Icon && (
        <div className="w-9 h-9 rounded-lg bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center text-cyan-300">
          <Icon className="w-4 h-4" />
        </div>
      )}
    </div>
  </Card>
);
